<template>
  <div>
    <v-container class="pt-3 pb-6 px-6">

      <!-- Floating Button -->
      <floating-button @click="clientCreate()" v-if="authUser && !isReadonly"/>

      <!-- Confirmation -->
      <confirmation
        v-if="authUser"
        v-model="dialog"
        title="Eliminar Cliente"
        text="Desea eliminar el cliente"
        :item="clientToDelete.business_name + ' / ' + clientToDelete.brand_name"
        @close="dialog = false"
        @confirm="deleteClient()"
      />

      <!-- Page Header -->
      <page-header
        title="Clientes"
        icon="mdi-view-list"
        divider="-"
        :breadcrumbs="breadcrumbs.items"
        refresh
        @refresh="loadClients()"
      ></page-header>

      <!-- Content -->
      <v-row align="center" justify="center">
        <!-- Table -->
        <v-col cols="12">
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <!-- Table -->
          <v-data-table
            v-if="authUser"
            class="elevation-1"
            :headers="headers"
            :items="filteredFarm"
            :items-per-page="5"
            :search="filterInput"
            :expanded.sync="expanded"
            :footer-props="{
              'items-per-page-options': [5, 15, 30, 50]
            }"
          >
            <!-- Top -->
            <template v-slot:top>
              <v-toolbar flat color="white">
                <!-- State Filter -->
                <v-select
                  v-model="filterState"
                  :items="stateItems"
                  class="shrink mr-5"
                  label="Estado"
                  clearable
                  hide-details
                  single-line
                ></v-select>
                <!-- Farm Filter -->
                <v-autocomplete
                  v-model="filterFarm"
                  :items="farms"
                  item-text="name"
                  item-value="id"
                  label="Parque"
                  class="shrink mr-5"
                  clearable
                  single-line
                  hide-details
                ></v-autocomplete>
                <!-- Input Filter -->
                <v-text-field
                  v-model="filterInput"
                  class="grow"
                  label="Buscar..."
                  prepend-icon="mdi-magnify"
                  clearable
                  single-line
                  hide-details
                ></v-text-field>
              </v-toolbar>
            </template>
            <!-- Expand Icon -->
            <template v-slot:item.expand="{ item }">
              <v-icon
                :class="{'rotate': expanded.includes(item)}"
                icon
                @click="expanded.includes(item) ? expanded = [] : expanded = [item]"
                v-if="item.wind_farms.length > 3 "
              >mdi-chevron-down</v-icon>
            </template>
            <!-- Farms -->
            <template v-slot:item.wind_farms="{ item }">
              <span v-for="(farm, idx) in item.wind_farms" :key="idx">
                <v-chip class="ma-1" v-if="idx < 3">{{ farm.name }}</v-chip>
              </span>
              <span class="ma-1" v-if="item.wind_farms.length > 3" >({{ item.wind_farms.length - 3 }} más)</span>
            </template>
            <!-- Expanded Farms Row -->
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="3"></td>
              <td :colspan="1" class="text-center py-3">
                <span class="mx-1" v-for="(farm, idx) in item.wind_farms" :key="idx">
                  <v-chip class="my-1" v-if="idx >= 3">{{ farm.name }}</v-chip>
                </span>
              </td>
              <td :colspan="2"></td>
            </template>
            <!-- State -->
            <template v-slot:item.is_active="{ item }">
              <v-chip
                :color="item.is_active ? 'primary' : 'deep-orange'"
                :class="{'white--text': !item.is_active}"
              >{{ item.is_active ? 'Activo' : 'Inactivo' }}</v-chip>
            </template>
            <!-- Actions -->
            <template v-slot:item.action="{ item }">
              <v-icon class="mr-2" @click="clientEdit(item.id)">mdi-account-edit</v-icon>
              <v-icon @click="deleteClientDialog(item)">mdi-delete</v-icon>
            </template>
            <!-- No Data -->
            <template v-slot:no-data>
              No hay elementos
            </template>
            <!-- No Results -->
            <template v-slot:no-results>
              No hay elementos
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import Confirmation from "@/components/Confirmation";
import PageHeader from "@/components/PageHeader";
import Util from "@/util/index";

export default {
  components: {
    FloatingButton,
    Confirmation,
    PageHeader
  },
  data: () => ({
    expanded: [],
    authUser: false,
    isLoadingData: false,
    dialog: false,
    clientToDelete: {},
    filterInput: '',
    filterFarm: '',
    filterState: null,
    stateItems: [
      {
        text: 'Activo',
        value: 'true'
      },
      {
        text: 'Inactivo',
        value: 'false'
      }
    ],
    defaultHeaders: [
      {
        text: '',
        align:'center',
        sortable: false,
        value: 'expand'
      },
      {
        text: 'Razón social',
        align: 'center',
        sortable: true,
        value: 'business_name'
      },
      {
        text: 'Nombre comercial',
        align: 'center',
        sortable: true,
        value: 'brand_name'
      },
      {
        text: 'Parques',
        align: 'center',
        sortable: true,
        value: 'wind_farms'
      },
      {
        text: 'Estado',
        align: 'center',
        sortable: true,
        value: 'is_active'
      },
      {
        text: 'Acción',
        align: 'center',
        sortable: false,
        value: 'action'
      }
    ],
    breadcrumbs: {
      items: [
        {
          text: "Configuración",
          disabled: true
        },
        {
          text: "Clientes",
          disabled: true
        }
      ]
    }
  }),
  computed: {
    isReadonly() {
      return this.$store.getters['IS_READONLY']
    },
    farms () {
      return this.$store.state.farms.wind_farms
    },
    clients () {
      return this.$store.state.clients.clients
    },
    filteredState () {
      return this.filterState
        ? this.clients.filter(element => {
          return element.is_active.toString().includes(this.filterState)
        }) : this.clients
    },
    filteredFarm () {
      return this.filterFarm
        ? this.filteredState.filter(element => {
            return element.wind_farms.some(element => {
              return element.id === this.filterFarm
            })
          }) : this.filteredState
    },
		headers() {
			return this.isReadonly ? this.defaultHeaders.filter(e => e.value !== 'action') : this.defaultHeaders
		}
  },
  methods: {
    deleteClientDialog (client) {
      this.clientToDelete = client
      this.dialog = !this.dialog
    },
    deleteClient () {
      var id = this.clientToDelete.id
      // Check if contract_award exist
      if (this.clientToDelete.contract_award) {
        // Check if contract_award has data
        if (this.clientToDelete.contract_award.lenght === 0) {
          this.$store.dispatch('clients/deleteClient', id)
            .then(() => {
              this.$toast("Cliente borrado correctamente.", {
                color: "success",
                x: "center",
                y: "top",
                icon: "mdi-information"
              });
              this.dialog = !this.dialog
              this.loadClients()
            })
            .catch(error => {
              var errorToastMessage = "Ha ocurrido un error eliminando el cliente."
              Util.showErrorToast(this, error, errorToastMessage);
            })
            .finally(() => {
              this.dialog = !this.dialog
            })
        } else {
          this.$toast("No puedes borrar un cliente que tenga adjudicaciones.", {
            color: "error",
            x: "center",
            y: "top",
            icon: "mdi-information"
          });
          this.dialog = !this.dialog
        }
      } else {
        this.$store.dispatch('clients/deleteClient', id)
          .then(() => {
            this.$toast("Cliente borrado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.dialog = !this.dialog
            this.loadClients()
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error eliminando el cliente."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.dialog = !this.dialog
          })
      }
    },
    clientCreate () {
      this.$router.push({
        name: "client-create"
      });
    },
    clientEdit (id) {
      this.$router.push({
        name: "client-edit",
        params: { id: id }
      });
    },
    loadClients () {
      this.isLoadingData = true;
      this.$store.dispatch('clients/getClients')
        .then(() => {
          this.authUser = true
          this.loadFarms()
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando los clientes."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
    },
    loadFarms () {
      this.$store.dispatch('farms/getFarms')
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de parques eólicos."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.isLoadingData = false;
        });
    }
  },
  created () {
    this.loadClients()
  }
}
</script>
